import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import dayjs from 'dayjs';
import { getStoredToken } from '@/api';
import { ReactFlowProvider, useReactFlow, useNodesState, useEdgesState, addEdge } from 'reactflow';
import type { Node, Connection } from 'reactflow';

import 'reactflow/dist/style.css';
import { Button } from '@/components/ui/button';
import { Play, Save, Calendar, Loader2, LayoutTemplate, Braces, Trash2, Ban, ArrowLeft, ChevronDown, AlertTriangle, Maximize, Minimize, Download, History, FolderOpen, X, BookOpen, Lock, Settings, FileText, FilePen, Share2 } from 'lucide-react';
import FlowVersionsPanel, { type FlowVersion } from '../components/flows/FlowVersionsPanel';
import dagre from 'dagre';
import FlowSidebar from '../components/flows/FlowSidebar';
import FlowCanvas from '../components/flows/FlowCanvas';
import PropertiesPanel from '../components/flows/PropertiesPanel';
import BottomPanel from '../components/flows/BottomPanel';
import { useNotification } from '@/context/NotificationContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import DashboardGrid from '../components/flows/dashboard/DashboardGrid';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FlowRunReportModal, { type FlowRunReport } from '../components/flows/FlowRunReportModal';
import PublishAppModal from '../components/flows/PublishAppModal';
import FlowLeaderboardModal from '../components/flows/FlowLeaderboardModal';
import { Rocket } from 'lucide-react';
import ResizableSidebar from '../components/ui/ResizableSidebar';
// @ts-ignore
import { ConfirmModal } from '../components/Common.jsx';
import PublishedEditWarningModal from '../components/PublishedEditWarningModal';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useInteractiveRunner } from '../hooks/useInteractiveRunner';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';
import { useAuth } from '@/context/useAuth';
import { DashboardShareSheet } from '@/components/DashboardStudio/DashboardShareSheet';
import { apiFetch } from '@/api';
import pkg from '../../package.json';
const BUILD_VERSION = pkg.version;



export default function FlowEditorPage(props: any) {
    return (
        <ReactFlowProvider>
            <FlowEditorPageContent {...props} />
        </ReactFlowProvider>
    );
}

function FlowEditorPageContent({ flowId: propFlowId, onBack, studioMode, refreshContent, forceReadOnly, onRun, externalStepStatus, externalIsExecuting }: any) {
    const { getNodes, getEdges } = useReactFlow(); // Use store accessors for reading current state in async functions
    // --- Auto-Upload Interceptor ---
    // Called before every run. For any FileSource node with loadAtEveryRun=true:
    //   1. Try to read the file from the stored Workspace Directory Handle.
    //   2. If not found or directory missing, ask user to select their Workspace folder.
    //   3. Upload the fresh file to S3 and inject the S3 URI into the graph payload.
    const processAutoUploads = async (graph: any) => {
        let modifiedGraph = { ...graph };
        modifiedGraph.nodes = [...graph.nodes];

        for (let i = 0; i < modifiedGraph.nodes.length; i++) {
            const node = modifiedGraph.nodes[i];
            if (!((node.type === 'fileSource' || node.type === 'FILE_SOURCE' || node.type === 'file_source' || node.config?.connectionMode === 'file') && node.config?.loadAtEveryRun && node.config?.localFilePath)) continue;

            const { resolveFileInDirectory, saveHandle } = await import('@/utils/localFileStore');
            const fileName = node.config.localFilePath;
            const dirKey = `dir-${node.id}`;

            let file: File | null = null;

            // Step 1 — try to dynamically resolve the file by name inside the authorized directory
            file = await resolveFileInDirectory(dirKey, fileName);

            // Step 2 — if no file found, we need to authorize the directory
            if (!file) {
                addLog(`[Auto-Upload] Could not find '${fileName}'. Please authorize the folder containing this file...`, 'Info');
                try {
                    // @ts-ignore
                    const dirHandle = await window.showDirectoryPicker({
                        id: 'flow-workspace',
                        mode: 'read',
                        startIn: 'downloads'
                    });
                    await saveHandle(dirKey, dirHandle);
                    
                    // Try resolving again now that we have the directory
                    file = await resolveFileInDirectory(dirKey, fileName);
                    if (!file) {
                        const msg = `The file '${fileName}' was not found in the folder you selected ('${dirHandle.name}').`;
                        addLog(`[Auto-Upload] ${msg}`, 'Error');
                        throw new Error(msg);
                    }
                } catch (pickErr: any) {
                    if (pickErr.name === 'AbortError') {
                        const msg = `Folder selection cancelled. Run aborted.`;
                        addLog(`[Auto-Upload] ${msg}`, 'Error');
                        throw new Error(msg);
                    }
                    const msg = `Could not access folder: ${pickErr.message}`;
                    addLog(`[Auto-Upload] ${msg}`, 'Error');
                    throw new Error(msg);
                }
            }

            // Step 3 — upload the fresh file to S3
            const lastMod = new Date(file.lastModified).toLocaleString();
            addLog(`[Auto-Upload] Uploading fresh '${file.name}' (modified: ${lastMod}, size: ${(file.size / 1024).toFixed(1)} KB)...`, 'Info');
            const formData = new FormData();
            formData.append('file', file);
            const tok = getAuthToken() || null;
            let res: any;
            try {
                res = await apiFetch('/flows/ops/upload', tok, { method: 'POST', body: formData });
            } catch (uploadErr: any) {
                const msg = `Upload failed for '${file.name}': ${uploadErr.message}`;
                addLog(`[Auto-Upload] ${msg}`, 'Error');
                throw new Error(msg);
            }
            if (!res?.filePath) {
                const msg = `Upload did not return a file path for '${file.name}'. Check backend logs.`;
                addLog(`[Auto-Upload] ${msg}`, 'Error');
                throw new Error(msg);
            }

            modifiedGraph.nodes[i] = {
                ...node,
                config: { ...node.config, filePath: res.filePath, fileName: file.name, loadAtEveryRun: false }
            };
            addLog(`[Auto-Upload] Uploaded '${file.name}' to S3: ${res.filePath}`, 'Info');
        }

        return modifiedGraph;
    };
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const { user, token } = useAuth();
    const [isShareSheetOpen, setIsShareSheetOpen] = useState(false);
    const [shareUserCandidates, setShareUserCandidates] = useState<any[]>([]);
    const [sharedWith, setSharedWith] = useState<string[]>([]);
    const [publishShareMode, setPublishShareMode] = useState<'me' | 'all'>('me');

    const onConnect = useCallback(
        (params: Connection) => setEdges((eds) => {
            // Prevent duplicate edges: skip if an edge with the same endpoints already exists
            const isDuplicate = eds.some(
                (e) => e.source === params.source &&
                        e.target === params.target &&
                        e.sourceHandle === params.sourceHandle &&
                        e.targetHandle === params.targetHandle
            );
            if (isDuplicate) return eds;
            return addEdge(params, eds);
        }),
        [setEdges],
    );

    // When edges are removed, clear execution status on affected nodes so stale "Success" icons don't persist
    const onEdgesChangeWithStatusClear = useCallback(
        (changes: any[]) => {
            const removes = changes.filter((c: any) => c.type === 'remove');
            if (removes.length > 0) {
                const idsToRemove = removes.map((c: any) => c.id);
                const currentEdges = edgesRef.current;
                const affectedNodeIds = new Set<string>();
                currentEdges.filter((e: any) => idsToRemove.includes(e.id)).forEach((e: any) => {
                    affectedNodeIds.add(e.source);
                    affectedNodeIds.add(e.target);
                });
                if (affectedNodeIds.size > 0) {
                    setNodes((nds) => nds.map((n: any) =>
                        affectedNodeIds.has(n.id)
                            ? { ...n, data: { ...n.data, executionStatus: undefined, rowsAffected: undefined } }
                            : n
                    ));
                }
            }
            onEdgesChange(changes);
        },
        [onEdgesChange, setNodes],
    );

    // Actually, 'nodes' in closure is fine for most things, but if runFlow is not depending on nodes, it might be stale.
    // Ideally we should rely on the state variable 'nodes' directly in JSX, and use functional updates for setters.
    // For reads in async functions, 'nodes' variable might be stale closure.
    // Ref strategy for current nodes?
    const nodesRef = useRef(nodes);
    useEffect(() => { nodesRef.current = nodes; }, [nodes]);
    const edgesRef = useRef(edges);
    useEffect(() => { edgesRef.current = edges; }, [edges]);

    // Replace original getNodes/getEdges usages with safe getters if we want to be safe,
    // OR just rely on 'getStoreNodes' from ReactFlow which tracks the Controlled Props.
    // If ReactFlow tracks controlled props correctly, getStoreNodes() is best.

    // Let's alias them back to what the rest of the file expects
    // But wait, the file uses 'getNodes()' which was from useReactFlow.
    const applyShareSettings = async (payload: any) => {
        if (!flowId) {
            addNotification('Please save the flow before sharing', 'warning');
            throw new Error('Save required');
        }
        try {
            const res = await apiFetch(`/flows/${flowId}/share-link`, token, {
                method: 'POST',
                body: JSON.stringify(payload),
            });

            const nextSharedWith = Array.isArray(res?.sharedWith) ? res.sharedWith : [];
            setSharedWith(nextSharedWith);
            setPublishShareMode(nextSharedWith.includes('*') ? 'all' : 'me');

            const relativeUrl = res?.relativeUrl;
            if (!relativeUrl || !String(relativeUrl).includes('st=')) {
                throw new Error('Failed to generate secure share link.');
            }

            addNotification('Share settings updated', 'success');
            return `${window.location.origin}${relativeUrl}`;
        } catch (err: any) {
            addNotification(err.message || 'Failed to update share settings', 'error');
            throw err;
        }
    };

    useEffect(() => {
        const loadShareCandidates = async () => {
            if (!isShareSheetOpen) return;
            if (!(user?.roleId && ['r1', 'r2', 'r3', 'r4', 'r7'].includes(user.roleId))) {
                setShareUserCandidates([]);
                return;
            }
            try {
                const users = await apiFetch('/users', token, { method: 'GET' });
                const list = Array.isArray(users) ? users : [];
                const candidates = list
                    .filter((u: any) => String(u?.status || '').toLowerCase() === 'active')
                    .map((u: any) => ({ id: String(u.id), name: u.name, email: u.email }));
                setShareUserCandidates(candidates);
            } catch {
                setShareUserCandidates([]);
            }
        };
        loadShareCandidates();
    }, [isShareSheetOpen, token, user?.roleId]);

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { addNotification } = useNotification();

    // Use prop flowId if available, otherwise searchParams (for legacy/direct link support if any)
    const { formatTime: formatTzTime, formatDate: formatTzDate } = useTimezoneFormatter();
    const [flowId, setFlowId] = useState<string | null>(propFlowId || searchParams.get('flowId'));
    const [flowName, setFlowName] = useState("New Flow");
    const [isRunning, setIsRunning] = useState(false);
    const isRunningPrevRef = useRef(false);
    // Guided tour (flowBuilderTour step 12): wait until a run fully settles (success or fail)
    useEffect(() => {
        const w = window as Window & { __flowTourRunSeq?: number; __flowTourRunLastFinishedSeq?: number };
        if (isRunning && !isRunningPrevRef.current) {
            w.__flowTourRunSeq = (w.__flowTourRunSeq || 0) + 1;
        }
        if (!isRunning && isRunningPrevRef.current) {
            w.__flowTourRunLastFinishedSeq = w.__flowTourRunSeq || 0;
        }
        isRunningPrevRef.current = isRunning;
    }, [isRunning]);
    const [isSaving, setIsSaving] = useState(false);
    const [showPublishModal, setShowPublishModal] = useState(false);
    const [flowVersionNumber, setFlowVersionNumber] = useState<number>(0);
    const [activeFlowVersion, setActiveFlowVersion] = useState<string | null>(null);
    const [flowDescription, setFlowDescription] = useState<string>(""); // flow-level description
    const [showFlowSettings, setShowFlowSettings] = useState(false);     // settings dialog open
    const [editFlowName, setEditFlowName] = useState("");                 // temp edit in dialog
    const [editFlowDescription, setEditFlowDescription] = useState("");   // temp edit in dialog
    // Published-state: flow is read-only, a draft copy can be created for editing.
    // Initialize to null when loading an existing flow — status not yet known from API.
    const [flowStatus, setFlowStatus] = useState<string | null>(
        forceReadOnly ? 'published' : (propFlowId ? null : 'draft')
    );
    // Derived: whether the flow is in published (read-only) mode.
    // forceReadOnly lets tenant portal open any flow in view-only mode.
    const isPublished = flowStatus === 'published' || !!forceReadOnly;
    // True while the flow status is still being resolved from the API
    const isStatusLoading = flowStatus === null;
    // State for creating a draft copy of a published flow
    const [isCreatingDraft, setIsCreatingDraft] = useState(false);
    // Version History & Load Snapshot
    const [showVersionsPanel, setShowVersionsPanel] = useState(false);
    const [showLoadSnapshot, setShowLoadSnapshot] = useState(false);
    const [snapshotBanner, setSnapshotBanner] = useState<string | null>(null);
    // View State: 'canvas' | 'dashboard'
    const [activeView, setActiveView] = useState<'canvas' | 'dashboard'>('canvas');



    const [logs, setLogs] = useState<{ time: string, message: string, level: 'Info' | 'Warning' | 'Error' }[]>([]);
    const [nodeStats, setNodeStats] = useState<Record<string, { rows: number, cols: number, duration: string, status?: string }>>({});
    const [nodePreviews, setNodePreviews] = useState<Record<string, any[]>>({});

    const [selectedNode, setSelectedNode] = useState<Node | null>(null);
    const [isPropertiesOpen, setIsPropertiesOpen] = useState(true); // Default Open
    const [showInfoPanel, setShowInfoPanel] = useState(false);

    // Auto-open when node selected; reset to Config tab
    useEffect(() => {
        if (selectedNode) {
            setIsPropertiesOpen(true);
            setShowInfoPanel(false);
        }
    }, [selectedNode?.id]);

    const [isBottomPanelOpen, setIsBottomPanelOpen] = useState(false);
    const [isPinned, setIsPinned] = useState(true);
    const [consoleSize, setConsoleSize] = useState({ height: 250, width: 350 });

    const [isConsoleFloating, setIsConsoleFloating] = useState(false);
    const [floatPos, setFloatPos] = useState({ x: 0, y: 0 });
    const isDraggingConsoleRef = useRef(false);
    const consoleDragOriginRef = useRef({ mx: 0, my: 0, px: 0, py: 0 });

    // Track processed events to prevent duplicates
    const processedEventsRef = useRef<Set<string>>(new Set());
    // Track last completed execution to prevent duplicate toasts
    const lastCompletedExecutionRef = useRef<string | null>(null);

    const [showSchedule, setShowSchedule] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);

    const [scheduleCron, setScheduleCron] = useState("");
    const [isScheduleActive, setIsScheduleActive] = useState(true);

    // Audit / Run Report State
    const [showReportModal, setShowReportModal] = useState(false);

    // Leaderboard State
    const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
    const [leaderboardNodeId, setLeaderboardNodeId] = useState<string | null>(null);

    // Global Connections (passed to panels)
    const [connections, setConnections] = useState<any[]>([]);

    const [lastRunReport, setLastRunReport] = useState<FlowRunReport | null>(null);

    // Flow Variables (SSIS style)
    const [flowVariables, setFlowVariables] = useState<{ name: string, type: string, value: any }[]>([]);
    const [showVarModal, setShowVarModal] = useState(false);

    // Execution State
    const [currentExecutionId, setCurrentExecutionId] = useState<string | null>(null);
    const executionIdRef = useRef<string | null>(null);

    // Unsaved Changes Tracking
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const savedSnapshotRef = useRef<string>('');
    const [showExitWarning, setShowExitWarning] = useState(false);

    /** True while fetching an existing flow from the API (new flow / no id stays false). */
    const [flowCanvasLoading, setFlowCanvasLoading] = useState(false);

    // Global Row Limit (applies to ALL executions during development)
    const [rowLimit, setRowLimit] = useState<number | null>(1000);  // Default: 1000 rows for dev speed
    const [showRowLimitMenu, setShowRowLimitMenu] = useState(false);
    const rowLimitMenuRef = useRef<HTMLDivElement>(null);
    useEffect(() => { executionIdRef.current = currentExecutionId; }, [currentExecutionId]);


    // Flow Schema (Source of Truth for Node UI)
    const [flowSchema, setFlowSchema] = useState<Record<string, any>>({});
    useEffect(() => {
        const fetchSchema = async () => {
            try {
                const token = getAuthToken();
                const res = await fetch(getApiUrl('/api/flows/schema'), {
                    headers: { 'Authorization': `Bearer ${token}` } // Optional if public, but good practice
                });
                if (res.ok) {
                    const data = await res.json();
                    setFlowSchema(data);
                }
            } catch (e) {
                console.error("Failed to fetch flow schema", e);
            }
        };
        fetchSchema();
    }, []);

    const handleNodeUpdate = useCallback((nodeId: string, dataUpdate: any) => {
        setNodes((nds) => nds.map((n) => {
            if (n.id === nodeId) {
                return { ...n, data: { ...n.data, ...dataUpdate } };
            }
            return n;
        }));
    }, [setNodes]);

    const handleCancel = async () => {
        if (!currentExecutionId) return;

        const token = getAuthToken();
        try {
            await fetch(getApiUrl(`/api/flows/executions/${currentExecutionId}/cancel`), {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            addNotification("Cancellation Requested...", "info");
            addLog("Cancellation Requested by User.", "Warning");
        } catch (e) {
            addNotification("Failed to cancel execution", "error");
        }
    };

    const getAuthToken = () => getStoredToken();

    const getApiUrl = (path: string) => {
        let base = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
        // Normalize base
        if (base.endsWith('/')) base = base.slice(0, -1);

        // Smart Merge for /api/v1
        if (base.endsWith('/api/v1') && path.startsWith('/api/v1')) {
            return `${base}${path.substring(7)}`; // remove /api/v1
        }

        // Handle legacy /api/flows -> /api/flows compatibility if base includes v1
        if (base.endsWith('/api/v1') && path.startsWith('/api/')) {
            return `${base}${path.substring(4)}`; // remove /api
        }

        return `${base}${path}`;
    };

    // Load Flow if ID exists
    useEffect(() => {
        if (!flowId) {
            setFlowCanvasLoading(false);
            return;
        }

        const token = getAuthToken();
        if (!token) {
            setFlowCanvasLoading(false);
            return;
        }

        let cancelled = false;
        setFlowCanvasLoading(true);

        const shareToken = searchParams.get('st');
        const fetchUrl = shareToken
            ? getApiUrl(`/api/flows/${flowId}?st=${encodeURIComponent(shareToken)}`)
            : getApiUrl(`/api/flows/${flowId}`);

        fetch(fetchUrl, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        })
            .then(res => {
                if (!res.ok) throw new Error("Failed to load flow");
                return res.json();
            })
            .then(data => {
                if (cancelled) return;
                setFlowName(data.name);
                setFlowDescription(data.description || "");
                setScheduleCron(data.scheduleCron || "0 0 * * * ?");
                setIsScheduleActive(data.isActive);
                setSharedWith(data.sharedWith || []);
                setPublishShareMode(data.sharedWith?.includes('*') ? 'all' : 'me');
                if (data.version_number) {
                    setFlowVersionNumber(data.version_number);
                    // Immediately show badge from version_number (no extra API call needed)
                    setActiveFlowVersion(`v${data.version_number}`);
                    // Refine with exact label from version history
                    if (flowId) {
                        const versionsUrl = shareToken
                            ? getApiUrl(`/api/flows/${flowId}/versions?st=${encodeURIComponent(shareToken)}`)
                            : getApiUrl(`/api/flows/${flowId}/versions`);
                        fetch(versionsUrl, {
                            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                        })
                            .then(r => r.ok ? r.json() : [])
                            .then((versions: any[]) => {
                                if (Array.isArray(versions) && versions.length > 0) {
                                    const activeVer = versions.find(v => v.is_active)
                                        || versions.reduce((a, b) => b.version_number > a.version_number ? b : a);
                                    if (activeVer) setActiveFlowVersion(activeVer.version || `v${activeVer.version_number}`);
                                }
                            })
                            .catch(() => {});
                    }
                }
                // Set status — published flows open in read-only view mode.
                // When forceReadOnly (View Flow from App Runner), NEVER overwrite the
                // 'published' status we set initially — the underlying flow may be a
                // draft but we must keep it view-only for tenants.
                const status = (data.status || '').toLowerCase();
                if (!forceReadOnly) setFlowStatus(status);
                // No banners, no modals — just open cleanly in view-only mode
                const graph = JSON.parse(data.graphJson || "{}");

                // ── Step 1: Run ID migration FIRST on graph.nodes before setNodes ──────
                // Old conditions may have a numeric Date.now() id or no id at all.
                // Upgrade to proper UUIDs so handle IDs become stable strings.
                // This MUST run before setNodes so cleanNodes already has migrated IDs ✅
                const idNeedsMigration = (id: any) =>
                    id === undefined || id === null || typeof id === 'number';

                (graph.nodes || []).forEach((n: any) => {
                    const t = (n.type || '').toLowerCase().replace(/_/g, '');
                    if (
                        ['conditionalrouter', 'conditionalsplit', 'split'].includes(t) &&
                        Array.isArray(n.data?.conditions)
                    ) {
                        n.data.conditions = n.data.conditions.map((c: any) =>
                            idNeedsMigration(c.id) ? { ...c, id: crypto.randomUUID() } : c
                        );
                    }
                });

                // Clean up execution artifacts (stats, labels, status icons) on load.
                // cleanNodes is built AFTER migration so setNodes gets migrated condition IDs ✅
                if (graph.nodes) {
                    const cleanNodes = graph.nodes.map((n: any) => ({
                        ...n,
                        data: {
                            ...n.data,
                            outputStats: undefined,
                            executionStatus: undefined,
                            rowsAffected: undefined,
                            previewSnippet: undefined
                        }
                    }));
                    setNodes(cleanNodes);
                }

                if (graph.edges) {
                    // ── Step 2: Build lookup maps after ID normalisation ──────────────
                    const splitNodeConditions: Record<string, any[]> = {};
                    const routerNodeConditions: Record<string, { conditions: any[]; defaultRoute: string }> = {};

                    (graph.nodes || []).forEach((n: any) => {
                        const t = n.type || '';
                        // Conditional Split migration (condition-{idx} → condition-{cond.id})
                        if (['split', 'SPLIT', 'conditionalsplit', 'CONDITIONAL_SPLIT'].includes(t) && n.data?.conditions?.length) {
                            splitNodeConditions[n.id] = n.data.conditions;
                        }
                        // Conditional Router migration (routeName string → condition-{cond.id})
                        if (['conditionalRouter', 'CONDITIONAL_ROUTER', 'conditionalrouter'].includes(t) && n.data?.conditions?.length) {
                            routerNodeConditions[n.id] = {
                                conditions: n.data.conditions,
                                defaultRoute: n.data.defaultRoute || 'default'
                            };
                        }
                    });

                    // ── Step 3: Remap edge sourceHandles ─────────────────────────────
                    const cleanEdges = graph.edges.map((e: any) => {
                        let sourceHandle = e.sourceHandle;

                        // Migrate Split: condition-{idx} → condition-{cond.id}
                        if (sourceHandle && /^condition-\d+$/.test(sourceHandle)) {
                            const conditions = splitNodeConditions[e.source];
                            if (conditions) {
                                const idx = parseInt(sourceHandle.replace('condition-', ''), 10);
                                if (conditions[idx]?.id) {
                                    sourceHandle = `condition-${conditions[idx].id}`;
                                }
                            }
                        }

                        // Migrate Router: routeName string → condition-{cond.id} (or 'default')
                        if (sourceHandle && routerNodeConditions[e.source]) {
                            const { conditions, defaultRoute } = routerNodeConditions[e.source];

                            // Old routeName (not already prefixed or 'default') → condition-{uuid}
                            if (!sourceHandle.startsWith('condition-') && !sourceHandle.startsWith('route-') && sourceHandle !== 'default') {
                                if (sourceHandle === defaultRoute) {
                                    sourceHandle = 'default';
                                } else {
                                    const matched = conditions.find((c: any) => c.routeName === sourceHandle);
                                    if (matched?.id) sourceHandle = `condition-${matched.id}`;
                                }
                            }

                            // route-{numericTimestamp} → condition-{uuid}  (Date.now() bug from older saves)
                            if (sourceHandle.startsWith('route-')) {
                                const oldId = sourceHandle.replace('route-', '');
                                if (/^\d+$/.test(oldId)) {
                                    // Pure numeric — try routeName fallback
                                    const matchedByRouteName = conditions.find(
                                        (c: any) => c.routeName && `route-${c.routeName}` === sourceHandle
                                    );
                                    if (matchedByRouteName?.id) {
                                        sourceHandle = `condition-${matchedByRouteName.id}`;
                                    }
                                } else {
                                    // route-{uuid} → condition-{uuid} (just change the prefix)
                                    const matched = conditions.find((c: any) => c.id === oldId);
                                    if (matched?.id) sourceHandle = `condition-${matched.id}`;
                                }
                            }
                        }

                        return {
                            ...e,
                            sourceHandle,
                            label: undefined,
                            labelStyle: undefined,
                            style: { ...e.style, stroke: '#b1b1b7', strokeWidth: 1 }
                        };
                    });
                    // Deduplicate by source+sourceHandle+target+targetHandle to fix React key collisions
                    const seen = new Set<string>();
                    const dedupedEdges = cleanEdges.filter((e: any) => {
                        const key = `${e.source}|${e.sourceHandle ?? ''}|${e.target}|${e.targetHandle ?? ''}`;
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                    });
                    setEdges(dedupedEdges);
                }


                if (graph.variables) setFlowVariables(graph.variables);
            })

            .catch(() => {
                if (!cancelled) addNotification("Could not load flow", "error");
            })
            .finally(() => {
                if (!cancelled) setFlowCanvasLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [flowId, setNodes, setEdges]);

    // Strip UI-only state (selected, dragging) before comparing snapshots.
    // Node clicks toggle `selected` which must NOT count as an unsaved change.
    const toFlowSnapshot = (ns: any[], es: any[], vars: any[]) =>
        JSON.stringify({
            nodes: ns.map(({ selected: _s, dragging: _d, ...rest }: any) => rest),
            edges: es,
            variables: vars,
        });

    // --- Save initial snapshot after flow loads ---
    useEffect(() => {
        if (nodes.length > 0 && savedSnapshotRef.current === '') {
            savedSnapshotRef.current = toFlowSnapshot(nodes, edges, flowVariables);
        }
    }, [nodes, edges, flowVariables]);

    // --- Track unsaved changes (debounced 300ms so selection clicks don't stall UI) ---
    // In forceReadOnly mode, never mark as unsaved — prevents the Back button being blocked.
    const unsavedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (forceReadOnly) return; // App Runner view-only: never dirty
        if (flowStatus === 'published') return; // Published flows are read-only: never dirty
        if (flowStatus === null) return; // Still loading — status not yet known
        if (savedSnapshotRef.current === '') return;
        if (unsavedTimerRef.current) clearTimeout(unsavedTimerRef.current);
        unsavedTimerRef.current = setTimeout(() => {
            const current = toFlowSnapshot(nodes, edges, flowVariables);
            setHasUnsavedChanges(current !== savedSnapshotRef.current);
        }, 300);
        return () => { if (unsavedTimerRef.current) clearTimeout(unsavedTimerRef.current); };
    }, [nodes, edges, flowVariables, forceReadOnly]);

    // --- Sync external execution step status to canvas nodes (View Flow live mode) ---
    useEffect(() => {
        if (!forceReadOnly || !externalStepStatus) return;
        setNodes((nds: any[]) => nds.map((n: any) => {
            const s = externalStepStatus[n.id];
            if (!s) return n;
            const st = (s.status || '').toLowerCase();
            const executionStatus =
                ['running', 'in_progress'].includes(st) ? 'running'
                : ['completed', 'success', 'done'].includes(st) ? 'success'
                : ['failed', 'error'].includes(st) ? 'failed'
                : undefined;
            return { ...n, data: { ...n.data, executionStatus, rowsAffected: s.rows_affected } };
        }));
    }, [externalStepStatus, forceReadOnly, setNodes]);

    // --- Browser close/refresh guard ---
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    // --- Close row limit menu when clicking outside ---
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (rowLimitMenuRef.current && !rowLimitMenuRef.current.contains(e.target as HTMLElement)) {
                setShowRowLimitMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ── Floating console drag logic ───────────────────────────────────────────
    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            if (!isDraggingConsoleRef.current) return;
            setFloatPos({
                x: consoleDragOriginRef.current.px + (e.clientX - consoleDragOriginRef.current.mx),
                y: consoleDragOriginRef.current.py + (e.clientY - consoleDragOriginRef.current.my),
            });
        };
        const onMouseUp = () => { isDraggingConsoleRef.current = false; };
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, []);

    const startConsoleDrag = (e: React.MouseEvent) => {
        isDraggingConsoleRef.current = true;
        consoleDragOriginRef.current = { mx: e.clientX, my: e.clientY, px: floatPos.x, py: floatPos.y };
        e.preventDefault();
    };

    const floatConsole = () => {
        setFloatPos({ x: Math.max(0, window.innerWidth - 450), y: Math.max(60, window.innerHeight - 580) });
        setIsBottomPanelOpen(true);
        setIsConsoleFloating(true);
    };

    const dockConsole = () => {
        setIsConsoleFloating(false);
        setIsBottomPanelOpen(true);
    };

    const onAutoLayout = () => {
        const nodes = getNodes();
        const edges = getEdges();
        if (nodes.length === 0) return;

        const dagreGraph = new dagre.graphlib.Graph();
        dagreGraph.setDefaultEdgeLabel(() => ({}));

        dagreGraph.setGraph({ rankdir: 'LR' }); // Left to Right

        nodes.forEach((node) => {
            // Assume strict width/height or measure. Standard node is approx 150x80 usually
            dagreGraph.setNode(node.id, { width: 180, height: 100 });
        });

        edges.forEach((edge) => {
            dagreGraph.setEdge(edge.source, edge.target);
        });

        dagre.layout(dagreGraph);

        const layoutedNodes = nodes.map((node) => {
            const nodeWithPosition = dagreGraph.node(node.id);
            // Shift slightly to ensure positive coordinates if needed, 
            // though dagre usually gives good relative coords.
            node.position = {
                x: nodeWithPosition.x - 90, // Center offset
                y: nodeWithPosition.y - 50,
            };
            return node;
        });

        setNodes([...layoutedNodes]);
        addNotification("Flow Auto-Aligned", "success");
    };

    /** Unlocks this published flow for editing by resetting status to draft on the SAME record.
     *  All FlowVersion history is preserved. No clone / new ID is created. */
    const handleCreateDraft = async () => {
        if (!flowId) return;
        setIsCreatingDraft(true);
        try {
            const token = getAuthToken();
            const res = await fetch(getApiUrl(`/api/flows/${flowId}/unpublish`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({})
            });
            if (!res.ok) throw new Error('Failed to unlock flow');
            // Same flow, same ID — just flip to edit mode in-place
            setFlowStatus('draft');
            addNotification('Flow unlocked for editing — version history preserved', 'success');
        } catch {
            addNotification('Failed to unlock flow for editing', 'error');
        } finally {
            setIsCreatingDraft(false);
        }
    };

    const saveFlow = async (overrideName?: string, overrideDesc?: string) => {
        const nameToUse = typeof overrideName === 'string' ? overrideName : flowName;
        const descToUse = typeof overrideDesc === 'string' ? overrideDesc : flowDescription;

        if (nameToUse === "New Flow" && !flowId) {
            const now = new Date();
            const pad = (n: number) => n.toString().padStart(2, '0');
            const defaultName = `New Flow — ${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
            
            setEditFlowName(defaultName);
            setEditFlowDescription(descToUse || "");
            setShowFlowSettings(true);
            return;
        }

        const token = getAuthToken();
        if (!token) return;

        setIsSaving(true);
        try {
            const nodes = getNodes();
            const edges = getEdges();

            // Log Database Lookup nodes before saving
            const dbLookupNodes = nodes.filter(n => n.type === 'databaseLookup' || n.type === 'DATABASE_LOOKUP');
            console.log('[FlowEditor] Saving flow - Database Lookup nodes:', dbLookupNodes.length);
            dbLookupNodes.forEach(node => {
                console.log(`  - Node ${node.id}: connectionId =`, node.data?.connectionId, 'data=', node.data);
            });

            const graphJson = JSON.stringify({ nodes, edges, variables: flowVariables });

            // Map studioMode to Backend FlowType
            let type = 'workflow';
            if (studioMode === 'data_studio') type = 'data_pipeline';
            if (studioMode === 'ml_pipelines') type = 'ml_flow';
            if (studioMode === 'workflow_studio') type = 'workflow';

            // Find Schedule node to extract schedule_cron
            let nodeScheduleCron = "";
            let nodeIsActive = true;
            const scheduleNode = nodes.find(n => n.type?.toLowerCase() === 'schedule');
            if (scheduleNode && scheduleNode.data) {
                nodeScheduleCron = scheduleNode.data.cronExpression || scheduleNode.data.cron_expression || scheduleNode.data.cron || "";
                nodeIsActive = scheduleNode.data.isActive !== false;
            }

            const payload = {
                Name: nameToUse,
                Description: descToUse,
                GraphJson: graphJson,
                FlowType: type,
                // Pass these up so `create_flow` and `update_flow` have them to sync
                ScheduleCron: nodeScheduleCron,
                IsActive: nodeIsActive
            };

            let url = getApiUrl('/api/flows');
            let method = 'POST';

            if (flowId) {
                url += `/${flowId}`;
                method = 'PUT';
            }

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                const data = await res.json();
                if (!flowId) {
                    setFlowId(data.id);
                    // Do NOT update URL to avoid showing ID
                    // setSearchParams({ flowId: data.id }); 
                }
                // Update saved snapshot to mark as clean
                savedSnapshotRef.current = toFlowSnapshot(getNodes(), getEdges(), flowVariables);
                setHasUnsavedChanges(false);
                addNotification("Flow Saved Successfully", "success");
            } else {
                const errText = await res.text();
                throw new Error(`Save failed: ${errText}`);
            }
        } catch (e) {
            addNotification("Failed to save flow", "error");
            console.error(e);
        } finally {
            setIsSaving(false);
        }
    };

    const saveSchedule = async () => {
        if (!flowId) {
            addNotification("Please save the flow first", "error");
            return;
        }
        const token = getAuthToken();
        try {
            const res = await fetch(getApiUrl(`/api/flows/${flowId}/schedule`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ CronExpression: scheduleCron, IsActive: isScheduleActive })
            });
            if (res.ok) {
                addNotification("Schedule Updated", "success");
                setShowSchedule(false);
            } else throw new Error("Update failed");
        } catch (e) {
            console.error(e);
            addNotification("Failed to update schedule", "error");
        }
    };

    const handleExport = () => {
        let mappedType = 'workflow';
        if (studioMode === 'data_studio') mappedType = 'data_pipeline';
        if (studioMode === 'ml_pipelines') mappedType = 'ml_flow';
        if (studioMode === 'workflow_studio') mappedType = 'workflow';

        const exportData = {
            id: flowId,
            name: flowName,
            description: "",
            type: mappedType,
            nodes: getNodes(),
            edges: getEdges(),
            variables: flowVariables,
            build_version: BUILD_VERSION
        };
        const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(exportData, null, 2));
        const downloadAnchorNode = document.createElement('a');
        downloadAnchorNode.setAttribute("href", dataStr);
        downloadAnchorNode.setAttribute("download", `${flowName || 'flow'}_export.json`);
        document.body.appendChild(downloadAnchorNode); // required for firefox
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
        addNotification("Flow exported successfully", "success");
    };


    // Load connections on mount
    useEffect(() => {
        const token = getAuthToken();
        if (token) {
            fetch(getApiUrl('/api/datasources'), {
                headers: { 'Authorization': `Bearer ${token}` }
            })
                .then(async res => {
                    if (res.status === 401) {
                        addNotification("Session Expired. Please login again.", "error");
                        return [];
                    }
                    if (!res.ok) throw new Error("Failed to load datasources");
                    return res.json();
                })
                .then(data => setConnections(Array.isArray(data) ? data : []))
                .catch(err => console.error("Failed to load connections for run engine", err));
        }
    }, []);

    // Fetch Preview when Node Selected
    useEffect(() => {
        if (!selectedNode) return;

        // If we already have a non-empty preview, don't re-fetch
        if (nodePreviews[selectedNode.id]?.length > 0) return;

        // Check if node has executed successfully
        const stats = nodeStats[selectedNode.id];
        // Only fetch if status is Success or if we want to try anyway (sometimes partial data?)
        // Let's rely on stats existing.
        if (!stats || stats.status !== 'Success') return;

        const token = getAuthToken();
        // Use currentExecutionId directly for ad-hoc runs
        const execId = currentExecutionId;

        if (!execId) return;

        // console.log(`Fetching preview for ${selectedNode.id}...`);

        // Use the new endpoint
        fetch(getApiUrl(`/api/flows/preview/${execId}/${selectedNode.id}`), {
            headers: { 'Authorization': `Bearer ${token}` }
        })
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) {
                    setNodePreviews(prev => ({ ...prev, [selectedNode.id]: data }));

                    // IMPORTANT: For Visual Nodes, we need to embed this data into the node.data
                    // so that the VisualNode component (and Dashboard) receives it!
                    if (selectedNode.type === 'visual') {
                        setNodes((nds) => nds.map(n => n.id === selectedNode.id ? { ...n, data: { ...n.data, previewData: data } } : n));
                    }
                }
            })
            .catch(err => console.error("Preview fetch failed", err));

    }, [selectedNode, lastRunReport, nodeStats, currentExecutionId]);

    const addLog = useCallback((message: string, level: 'Info' | 'Warning' | 'Error' = 'Info') => {
        // Use the centralized timezone-aware formatter for execution logs
        const time = formatTzTime(dayjs().toISOString());
        setLogs(prev => [...prev, { time, message, level }]);
    }, [formatTzTime]);

    // --- Build Graph Payload (shared by runFlow, handleRunNode, testFlow) ---
    const buildGraphPayload = () => {
        const nodes = getNodes().map(node => ({
            id: node.id,
            name: node.data?.label || node.id,
            type: node.type,
            config: node.data || {},
            position: node.position,
            parentNodeId: node.parentNode
        }));

        return {
            id: flowId || undefined,
            name: flowName,
            nodes: nodes,
            edges: getEdges(),
            variables: flowVariables
        };
    };

    // --- Submit Execution to Backend ---
    const submitExecution = async (graph: any, options: { targetNodeId?: string; rowLimit?: number; mode?: string; executionId?: string } = {}) => {
        const token = getAuthToken();
        if (!token) throw new Error("No Auth Token");


        let runUrl = getApiUrl(flowId ? `/api/flows/${flowId}/run` : `/api/flows/adhoc/run`);

        // Use Production Endpoint if requested
        if (options.mode === 'PRODUCTION') {
            if (!flowId) throw new Error("Please save the flow before running in Production mode.");
            runUrl = getApiUrl(`/api/flows/${flowId}/execute`);
        }

        const body: any = { Flow: graph };
        if (options.targetNodeId) body.targetNodeId = options.targetNodeId;
        if (options.rowLimit) body.rowLimit = options.rowLimit;
        if (options.mode) body.executionMode = options.mode;
        if (options.executionId) body.executionId = options.executionId;

        const res = await fetch(runUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(body)
        });

        if (!res.ok) {
            const err = await res.text();
            throw new Error(err);
        }

        return await res.json();
    };

    // --- Run Flow (Interactive — uses Port 8001 WebSocket) ---
    const runFlow = async () => {
        setIsRunning(true);
        setLogs([]);
        setNodeStats({});
        setNodePreviews({});
        setIsBottomPanelOpen(true);
        // We no longer guess the executionId here. 
        // It is set dynamically by the 'CONNECTED' message from the Interactive Runner.
        executionIdRef.current = 'interactive_preview';
        setCurrentExecutionId('interactive_preview');

        // Reset node visuals
        setNodes((nds) => nds.map(n => ({
            ...n,
            data: { ...n.data, executionStatus: undefined, rowsAffected: undefined, outputStats: undefined, previewSnippet: undefined, error: undefined }
        })));

        const limitLabel = rowLimit ? ` (limit: ${rowLimit.toLocaleString()} rows)` : '';
        addLog(`Starting Flow Runner...${limitLabel}`, 'Info');

        try {
            const rawGraph = buildGraphPayload();
            const graph = await processAutoUploads(rawGraph);
            // Decode tenant_id from the stored JWT (avoids a separate fetch/context dep)
            let tenant_id = 'platform';
            let user_id: string | undefined = undefined;
            try {
                const tok = getAuthToken();
                if (tok) {
                    const payload = JSON.parse(atob(tok.split('.')[1]));
                    // NOTE: payload.sub is the user email — do NOT use it as tenant_id
                    tenant_id = payload.tenant_id || payload.tenantId || 'platform';
                    user_id = payload.id || payload.userId;
                }
            } catch { /* fallback already set */ }
            if (!runInteractiveRef.current) throw new Error('Interactive runner not ready');
            await runInteractiveRef.current(graph, tenant_id, rowLimit || undefined, undefined, user_id);
            // Real completion is handled by handleRunnerMessage (EXECUTION_COMPLETE)
        } catch (e: any) {
            addLog(`Run Failed: ${e.message}`, 'Error');
            setIsRunning(false);
            addNotification(e.message, 'error');
        }
    };


    const runFlowProduction = async () => {
        setIsRunning(true);
        setLogs([]);
        setNodeStats({});
        setNodePreviews({});
        setIsBottomPanelOpen(true);

        // Clear all node statuses
        setNodes((nds) => nds.map(n => ({
            ...n, data: { ...n.data, executionStatus: undefined, rowsAffected: undefined, previewSnippet: undefined, error: undefined }
        })));

        addLog(`Submitting Flow for PRODUCTION Execution...`, "Info");

        try {
            const rawGraph = buildGraphPayload();
            const graph = await processAutoUploads(rawGraph);
            // Clear the executionId early so the WS handler auto-binds the first event
            executionIdRef.current = null;
            setCurrentExecutionId(null);

            // Pass 'PRODUCTION' mode
            const data = await submitExecution(graph, { mode: 'PRODUCTION', rowLimit: rowLimit || undefined });

            // Ensure it's correctly set (fallback if WS hasn't hit yet)
            // Fix: set executionIdRef.current *immediately* upon receipt so incoming ws messages use the true ID
            // even if they arrive slightly after submitExecution resolves.
            executionIdRef.current = data.executionId;
            setCurrentExecutionId(data.executionId);
            
            // Due to fast background workers, JOB_COMPLETED might have already arrived over WS.
            if (lastCompletedExecutionRef.current !== data.executionId) {
                addLog(`Production Job Queued. Execution ID: ${data.executionId}`, "Info");
                addNotification("Production Run Started", "success");
            }

        } catch (e: any) {
            addLog(`Production Start Failed: ${e.message}`, "Error");
            setIsRunning(false);
            addNotification(e.message, "error");
        }
    };

    // --- Run Individual Node (with upstream dependency resolution) ---
    // Uses the interactive WebSocket runner so that unsaved config changes are applied.
    const handleRunNode = async (nodeId: string) => {
        setIsRunning(true);
        setLogs([]);
        setNodeStats({});
        setNodePreviews({});
        setIsBottomPanelOpen(true);
        // We no longer guess the executionId here. 
        // It is set dynamically by the 'CONNECTED' message from the Interactive Runner.
        executionIdRef.current = 'interactive_preview';
        setCurrentExecutionId('interactive_preview');

        // Reset all node visuals so stale status is cleared
        setNodes((nds) => nds.map(n => ({
            ...n,
            data: { ...n.data, executionStatus: undefined, rowsAffected: undefined, previewSnippet: undefined, error: undefined }
        })));

        const targetNode = getNodes().find(n => n.id === nodeId);
        const nodeName = targetNode?.data?.label || nodeId;
        addLog(`Running to node: ${nodeName} (with dependencies)...`, "Info");

        try {
            const graph = buildGraphPayload();
            let tenant_id = 'platform';
            let user_id: string | undefined = undefined;
            try {
                const tok = getAuthToken();
                if (tok) {
                    const payload = JSON.parse(atob(tok.split('.')[1]));
                    tenant_id = payload.tenant_id || payload.tenantId || 'platform';
                    user_id = payload.id || payload.userId;
                }
            } catch { /* fallback */ }

            if (!runInteractiveRef.current) throw new Error('Interactive runner not ready');
            // Pass nodeId as targetNodeId — runner stops after executing this node (and its ancestors)
            await runInteractiveRef.current(graph, tenant_id, rowLimit || undefined, nodeId, user_id);
        } catch (e: any) {
            addLog(`Run Node Failed: ${e.message}`, "Error");
            setIsRunning(false);
            addNotification(e.message, 'error');
        }
    };



    // --- WebSocket Integration ---
    const wsRef = useRef<WebSocket | null>(null);

    useEffect(() => {
        const token = getAuthToken();
        if (!token) return;

        // Connect to Notification Stream
        const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
        let wsBase = apiBase.replace('http', 'ws');

        // Fix double /api/v1 if present
        // If base has /api/v1, we just append /notifications/ws
        // If base is root, we append /api/notifications/ws
        const wsPath = wsBase.includes('/api/v1') ? '/notifications/ws' : '/api/notifications/ws';
        const wsUrl = `${wsBase}${wsPath}?token=${token}`;
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
            console.log("Connected to Notification Stream");
        };

        ws.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                handleSocketMessage(message);
            } catch (e) {
                console.error("Failed to parse WS message", e);
            }
        };

        ws.onclose = () => {
            console.log("Disconnected from Notification Stream");
            // Optional: Reconnect logic could go here
        };

        wsRef.current = ws;

        return () => {
            if (ws.readyState === 1 || ws.readyState === 0) ws.close(); // Close even if connecting
        };
    }, []);

    const fetchVisualPreviews = useCallback(async (execId: string) => {
        const token = getAuthToken();
        const visualNodes = nodesRef.current.filter(n => n.type === 'visual');
        if (visualNodes.length === 0) return;

        addLog(`Fetching data for ${visualNodes.length} visual nodes...`, 'Info');

        Promise.all(visualNodes.map(async (vNode) => {
            try {
                const res = await fetch(getApiUrl(`/api/flows/preview/${execId}/${vNode.id}?t=${dayjs().valueOf()}`), {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    if (Array.isArray(data)) return { id: vNode.id, data };
                }
            } catch (e) {
                console.error(`Failed to fetch visual data for ${vNode.id}`, e);
            }
            return null;
        })).then(results => {
            const validResults = results.filter(r => r !== null) as { id: string, data: any[] }[];
            if (validResults.length > 0) {
                setNodes((currentNodes) => currentNodes.map(n => {
                    const match = validResults.find(r => r.id === n.id);
                    if (match) return { ...n, data: { ...n.data, previewData: match.data } };
                    return n;
                }));
                // Also update nodePreviews so Bottom Panel's Data Preview tab shows data
                setNodePreviews(prev => {
                    const updated = { ...prev };
                    validResults.forEach(r => { updated[r.id] = r.data; });
                    return updated;
                });
                addLog(`Loaded data for ${validResults.length} charts.`, "Info");
            }
        });
    }, [addLog]);

    const handleSocketMessage = useCallback((msg: any) => {
        // Filter messages unrelated to current execution (unless global?)
        // Ideally Payload has executionId. 
        if (!msg.payload) return;

        const { type } = msg;
        const { executionId, nodeId, node_name, node_label, status, rowsAffected, endTime, resultSnippet, error, duration, cols, previewData, outputStats } = msg.payload;

        // Debug Log
        console.log(`[WS] [${formatTzTime(new Date())}] ${type} for Node: ${nodeId} Exec: ${executionId}`);

        // If we are currently submitting a run and don't know the ID yet, auto-bind the first incoming ID.
        // This handles the race where JOB_STARTED/JOB_COMPLETED arrives before the HTTP response sets executionIdRef.
        if (!executionIdRef.current && executionId) {
            console.log(`[WS] Auto-binding executionId from incoming message: ${executionId}`);
            executionIdRef.current = executionId;
            setCurrentExecutionId(executionId);
        }

        // Only track if matches current (except for Interactive, which might use a generic ID initially)
        const idMismatch = executionIdRef.current && executionId !== executionIdRef.current && executionIdRef.current !== 'interactive_preview';

        // JOB_COMPLETED / JOB_FAILED always reset isRunning — even on id mismatch.
        // This prevents the UI from staying stuck at 'Queued...' when the run finishes
        // before the HTTP response has set executionIdRef (fast production runs race condition).
        if (idMismatch && type !== 'JOB_COMPLETED' && type !== 'JOB_FAILED') {
            console.log(`[WS] Dropping message for Exec: ${executionId} (Current: ${executionIdRef.current})`);
            return;
        }

        if (type === 'JOB_STARTED') {
            // Auto-bind on JOB_STARTED so subsequent messages match
            if (executionId && executionIdRef.current !== executionId && executionIdRef.current !== 'interactive_preview') {
                executionIdRef.current = executionId;
                setCurrentExecutionId(executionId);
            }
            setIsRunning(true);
            return;
        }

        if (type === 'JOB_UPDATE') {
            // Update Node Stats
            setNodeStats(prev => ({
                ...prev,
                [nodeId]: {
                    rows: rowsAffected,
                    duration: endTime ? 'Done' : 'Running', // Calc duration if needed
                    status: 'Success', // JOB_UPDATE usually means success of step
                    startTime: dayjs().toISOString() // Approximate
                }
            }));

            // Update Node Visuals
            setNodes((nds) => nds.map((n) => {
                if (n.id === nodeId) {
                    console.log(`[WS] JOB_UPDATE: Updating Node ${nodeId} -> Success`);
                    return {
                        ...n,
                        data: {
                            ...n.data,
                            executionStatus: 'Success',
                            rowsAffected: rowsAffected,
                            previewSnippet: resultSnippet
                        }
                    };
                }
                // Check Edges (Output of this node)
                return n;
            }));

            // Update Edges
            setEdges((eds) => eds.map((e) => {
                if (e.source === nodeId) {
                    return {
                        ...e,
                        label: rowsAffected !== undefined ? `${rowsAffected} rows` : undefined,
                        labelStyle: { fill: '#64748b', fontWeight: 600, fontSize: 11 },
                        style: { ...e.style, stroke: '#64748b', strokeWidth: 1.5, animation: undefined }
                    };
                }
                return e;
            }));
        }
        else if (type === 'NODE_STARTED') {
            // Remove deduplication to fix UI state issues
            // (setNodes is idempotent)

            setNodes((currentNodes) => currentNodes.map(n => {
                if (n.id === nodeId) {
                    return { ...n, data: { ...n.data, executionStatus: 'RUNNING' } };
                }
                return n;
            }));
            // Use node name if available
            const displayName = node_label || node_name || nodeId;
            addLog(`Node Started: ${displayName}`, "Info");
        }
        else if (type === 'NODE_COMPLETED') {
            // Update Node Stats
            setNodeStats(prev => ({
                ...prev,
                [nodeId]: {
                    rows: rowsAffected || 0,
                    cols: cols || 0,
                    duration: duration || 'N/A',
                    status: 'Success'
                }
            }));

            // Update Node Visuals
            setNodes((nds) => nds.map((n) => {
                if (n.id === nodeId) {
                    console.log(`[WS] [${formatTzTime(new Date())}] NODE_COMPLETED: Updating Node ${nodeId} -> COMPLETED`);
                    const inferredOutputSchema = (previewData && Array.isArray(previewData) && previewData.length > 0)
                        ? Object.keys(previewData[0] || {}).map((name) => ({ name, type: 'string' }))
                        : n.data?.outputSchema;
                    return {
                        ...n,
                        data: {
                            ...n.data,
                            executionStatus: 'COMPLETED',
                            rowsAffected: rowsAffected,
                            outputStats: outputStats || n.data?.outputStats,
                            previewSnippet: resultSnippet,
                            previewData: (previewData && Array.isArray(previewData)) ? previewData : n.data?.previewData,
                            outputSchema: inferredOutputSchema
                        }
                    };
                }
                return n;
            }));

            // Update Edges — CONDITIONAL_SPLIT: label each edge with per-handle count from outputStats
            if (rowsAffected !== undefined && rowsAffected >= 0) {
                // Check if the completed node is a split node with per-handle stats
                const completedNode = nodesRef.current.find(n => n.id === nodeId);
                const nodeTypeUpper = String(completedNode?.type || '').toUpperCase();
                const isSplitNode = completedNode && (
                    nodeTypeUpper === 'CONDITIONAL_SPLIT' ||
                    nodeTypeUpper === 'CONDITIONALSPLIT' ||
                    nodeTypeUpper === 'SPLIT'
                );

                setEdges((eds) => eds.map((e) => {
                    if (e.source !== nodeId) return e;
                    // For split nodes: use per-handle count from outputStats if available
                    let edgeCount = rowsAffected;
                    if (isSplitNode && outputStats && e.sourceHandle) {
                        const handleCount = outputStats[e.sourceHandle];
                        if (handleCount !== undefined) edgeCount = handleCount;
                    }
                    return {
                        ...e,
                        label: `${edgeCount} rows`,
                        labelStyle: { fill: '#10b981', fontWeight: 600, fontSize: 11 },
                        style: { ...e.style, stroke: '#10b981', strokeWidth: 2 }
                    };
                }));
            }

            // Store preview data from Interactive Runner for Data Preview tab
            if (previewData && Array.isArray(previewData)) {
                setNodePreviews(prev => ({ ...prev, [nodeId]: previewData }));
            }

            const displayName = node_label || node_name || nodeId;
            const rowsText = (rowsAffected !== undefined && rowsAffected >= 0) ? ` (${rowsAffected} rows)` : '';
            addLog(`Node Completed: ${displayName}${rowsText}`, "Info");
        }
        else if (type === 'NODE_FAILED') {
            setNodes((currentNodes) => currentNodes.map(n => {
                if (n.id === nodeId) {
                    return { ...n, data: { ...n.data, executionStatus: 'FAILED', error } };
                }
                return n;
            }));

            const displayName = node_label || node_name || nodeId;
            addLog(`Node Failed: ${displayName} - ${error || 'Unknown Error'}`, "Error");
        }
        else if (type === 'JOB_FAILED') {
            setIsRunning(false);
            addNotification(error || "Flow Execution Failed!", "error");
            addLog(`Flow Execution Failed: ${error || 'Unknown Error'}`, "Error");
        }
        else if (type === 'JOB_COMPLETED') {
            // Use lastCompletedExecutionRef to prevent toast duplication
            if (lastCompletedExecutionRef.current === executionId) return;
            lastCompletedExecutionRef.current = executionId;

            setIsRunning(false);
            addNotification("Flow Completed Successfully!", "success");
            addLog("Flow Execution Completed", "Info");

            // Auto-fetch visuals
            const visualNodes = nodesRef.current.filter(n => n.type === 'visual');
            if (visualNodes.length > 0) {
                // Trigger fetch
                fetchVisualPreviews(executionId);
            }

            // Wait a moment for any straggling NODE_COMPLETED messages, then sweep any stuck RUNNING nodes
            setTimeout(() => {
                setNodes(nds => nds.map(n => {
                    const currentStatus = String(n.data?.executionStatus || '').toUpperCase();
                    if (currentStatus === 'RUNNING' || currentStatus === 'PENDING') {
                        return { ...n, data: { ...n.data, executionStatus: 'COMPLETED' } };
                    }
                    return n;
                }));
            }, 2000);

            // [SYNC] Fetch Execution Log to verify timezone localization
            const token = getStoredToken();
            fetch(getApiUrl(`/api/execution/logs?id=${executionId}`), {
                headers: { 'Authorization': `Bearer ${token}` }
            })
            .then(res => res.json())
            .then(data => {
                if (data && data.length > 0) {
                    const log = data[0];
                    console.log(`%c[Execution Log View] %cStarted: ${formatTzDate(log.start_time)}, Completed: ${formatTzDate(log.end_time)}, Status: ${log.status}`, "color: #0ea5e9; font-weight: bold;", "color: inherit;");
                }
            })
            .catch(e => console.debug("Log sync silent fail", e));
        }
        // Highlight failed node if possible?
    }, [addLog, addNotification, currentExecutionId, fetchVisualPreviews]);

    const handleRunnerMessage = useCallback((msg: any) => {
        if (msg.type === 'CONNECTED') {
            addLog('Connected to Flow Runner...', 'Info');
            if (msg.payload && msg.payload.runId) {
                const newExecId = `interactive_${msg.payload.runId}`;
                executionIdRef.current = newExecId;
                setCurrentExecutionId(newExecId);
                console.log(`[Interactive Runner] [${formatTzTime(new Date())}] Set Execution ID dynamically to: ${newExecId}`);
            }
            return;
        }
        if (msg.type === 'EXECUTION_COMPLETE') { 
            addLog('Flow execution complete.', 'Info'); 
            setIsRunning(false); 
            
            // [SYNC] Fetch Execution Log summary
            const currentExecId = executionIdRef.current;
            if (currentExecId) {
                const token = getStoredToken();
                fetch(getApiUrl(`/api/execution/logs?id=${currentExecId}`), {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                .then(res => res.json())
                .then(data => {
                    if (data && data.length > 0) {
                        const log = data[0];
                        console.log(`%c[Execution Log View] %cStarted: ${formatTzDate(log.start_time)}, Completed: ${formatTzDate(log.end_time)}, Status: ${log.status}`, "color: #0ea5e9; font-weight: bold;", "color: inherit;");
                    }
                })
                .catch(e => console.debug("Log sync silent fail", e));
            }
            return; 
        }
        if (msg.type === 'EXECUTION_PAUSED') { addLog(`Execution Paused: ${msg.message}`, 'Warning'); setIsRunning(false); return; }
        if (msg.type === 'ERROR') { addLog(`Runner error: ${msg.message}`, 'Error'); setIsRunning(false); return; }
        if (msg.type === 'NODE_STATUS' && msg.payload) {
            const p = msg.payload;
            const currentExecId = executionIdRef.current;
            if ('error' in p) {
                handleSocketMessage({ type: 'NODE_FAILED', payload: { ...p, executionId: currentExecId, nodeId: p.node_id, error: p.error } });
            } else if ('rows_affected' in p) {
                // Map to NODE_COMPLETED to trigger logging, UI updates, and Data Preview
                handleSocketMessage({ type: 'NODE_COMPLETED', payload: { ...p, executionId: currentExecId, nodeId: p.node_id, node_name: p.node_name, node_label: p.node_label, rowsAffected: p.rows_affected, output_table: p.output_table, previewData: p.previewData || p.preview_data, outputStats: p.output_stats } });
            } else {
                handleSocketMessage({ type: 'NODE_STARTED', payload: { ...p, executionId: currentExecId, nodeId: p.node_id, node_name: p.node_name, node_label: p.node_label } });
            }
        }
    }, [addLog, handleSocketMessage]);

    const { run: runInteractive } = useInteractiveRunner({
        onMessage: handleRunnerMessage,
        onComplete: (success) => { setIsRunning(false); if (!success) addNotification('Flow run failed', 'error'); },
    });

    const runInteractiveRef = useRef<((graph: any, tenant_id: string, rowLimit?: number, targetNodeId?: string, user_id?: string) => Promise<string | void>) | null>(null);

    // Sync ref so runFlow() always calls the latest version
    runInteractiveRef.current = runInteractive;

    useEffect(() => {
        const checkRunStatus = async () => {
            if (!isRunning || !currentExecutionId || currentExecutionId === 'interactive_preview') return;
            
            try {
                const token = getStoredToken();
                if (!token || !flowId) return;
                
                const runs = await apiFetch(`/flows/${flowId}/runs`, token);
                if (runs && Array.isArray(runs)) {
                    const currentRun = runs.find((r: any) => r.id === currentExecutionId);
                    
                    if (currentRun && (currentRun.status === 'Completed' || currentRun.status === 'Failed' || currentRun.status === 'COMPLETED' || currentRun.status === 'FAILED')) {
                        // Prevent duplicates
                        if (lastCompletedExecutionRef.current === currentExecutionId) {
                            setIsRunning(false);
                            return;
                        }
                        
                        lastCompletedExecutionRef.current = currentExecutionId;
                        setIsRunning(false);
                        
                        if (currentRun.status.toLowerCase() === 'completed') {
                            addNotification("Flow Completed Successfully!", "success");
                            addLog("Flow Execution Completed", "Info");
                            // Auto-fetch visuals
                            const visualNodes = nodesRef.current.filter(n => n.type === 'visual');
                            if (visualNodes.length > 0) {
                                fetchVisualPreviews(currentExecutionId);
                            }
                        } else {
                            addNotification("Flow Execution Failed!", "error");
                            addLog("Flow Execution Failed", "Error");
                        }
                    }
                }
            } catch (e) {
                console.error("[Polling] Failed to check run status:", e);
            }
        };

        let interval: any;
        if (isRunning && currentExecutionId && currentExecutionId !== 'interactive_preview') {
            interval = setInterval(checkRunStatus, 3000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isRunning, currentExecutionId, flowId, addLog, addNotification, fetchVisualPreviews]);

    const handlePublishApp = async (name: string, description: string, contentTypeId: number, sharedWith: string[], folderName: string, status?: string, overwrite = false, linkedDashboardId?: string, versionLabel?: string) => {
        const token = getAuthToken();
        if (!token) return;
        if (!flowId) {
            addNotification("Please save the flow before publishing.", "error");
            return;
        }

        try {
            const res = await fetch(getApiUrl(`/api/flows/${flowId}/publish`), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    name,
                    description,
                    content_type_id: contentTypeId,
                    sharedWith,
                    folderName,
                    status,
                    linked_dashboard_id: linkedDashboardId,
                    version_label: versionLabel || undefined,
                })
            });

            if (res.ok) {
                const resData = await res.json();
                if (resData.content_id) {
                    window.dispatchEvent(new Event('refresh-sidebar'));
                }
                setSharedWith(resData.sharedWith || []);
                setPublishShareMode(resData.sharedWith?.includes('*') ? 'all' : 'me');
                addNotification(`Flow published successfully as version ${resData.version_label}`, 'success');

                const newLabel = versionLabel?.trim() || `v${(flowVersionNumber || 0) + 1}`;
                setActiveFlowVersion(newLabel);
                setFlowVersionNumber(prev => prev + 1);
                setShowPublishModal(false);
                if (refreshContent) refreshContent();
                window.dispatchEvent(new Event('refresh-sidebar'));
            } else if (res.status === 409) {
                // Read the actual backend message so the modal can distinguish
                // version-label conflicts from name conflicts
                let detail = "409 Conflict";
                try {
                    const body = await res.json();
                    detail = `409 ${body.detail || 'Conflict'}`;
                } catch { /* ignore parse errors */ }
                throw new Error(detail);
            } else {
                const err = await res.text();
                throw new Error(err || "Failed to publish app");
            }
        } catch (e: any) {
            console.error(e);
            if (e.message.includes("409")) throw e;
            addNotification("Error publishing Flow App", "error");
        }
    };


    return (
        <div className={`flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 ${isFullScreen ? 'fixed inset-0 z-50 h-screen w-screen' : 'h-full'}`}>
            {/* Snapshot loaded warning banner */}
            {snapshotBanner && (
                <div className="flex items-center justify-between px-4 py-2 bg-amber-50 border-b border-amber-200">
                    <div className="flex items-center gap-2 text-amber-700 text-sm">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                        <span>⚠ {snapshotBanner} — edit and publish to save as a new version.</span>
                    </div>
                    <button onClick={() => setSnapshotBanner(null)} className="text-amber-500 hover:text-amber-700">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}


            {/* ─── Editing Toolbar ─────────────────────────────── */}
            <div className="h-14 border-b border-gray-200 flex items-center justify-between px-4 bg-gray-50/50">
                <div className="flex items-center gap-2">
                    {onBack && (
                        <Button variant="ghost" size="sm" onClick={() => {
                            // Never block navigation in view-only or published mode
                            if (hasUnsavedChanges && !forceReadOnly && flowStatus !== 'published') {
                                setShowExitWarning(true);
                            } else {
                                if (onBack) onBack();
                            }
                        }} className="mr-2">
                            <ArrowLeft className="h-4 w-4 mr-1" /> Back
                        </Button>
                    )}
                    <h1 className="font-semibold text-gray-900 mr-1 flex-shrink-0">
                    {isStatusLoading ? null : isPublished && <Lock className="w-4 h-4 text-amber-500 inline mr-1" />}
                        Flow:
                    </h1>
                    {/* 2-row block: name row + description subtitle */}
                    <div className="flex flex-col justify-center">
                        {/* Row 1: name + badge + Settings button */}
                        <div className="flex items-center gap-1.5">
                            <input
                                id="flow-editor-name"
                                className={`bg-transparent border-b border-transparent text-sm font-medium w-40 transition-colors dark:text-slate-200 ${
                                    isPublished
                                        ? 'opacity-70 cursor-not-allowed'
                                        : 'hover:border-gray-300 focus:border-blue-500 focus:outline-none'
                                }`}
                                value={flowName}
                                onChange={(e) => !isPublished && setFlowName(e.target.value)}
                                readOnly={isPublished}
                            />
                            {/* Status badges — hidden until flow status is known */}
                            {!isStatusLoading && (activeFlowVersion ? (
                                <span
                                    title={`Active published version: ${activeFlowVersion}`}
                                    className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-400 text-emerald-800 leading-none shadow-sm flex-shrink-0 whitespace-nowrap"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 animate-pulse"></span>
                                    {activeFlowVersion}
                                </span>
                            ) : (
                                <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-600 leading-none flex-shrink-0 whitespace-nowrap">
                                    Draft
                                </span>
                            ))}
                            {!isStatusLoading && isPublished && (
                                <span className="inline-flex items-center text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-amber-100/80 border border-amber-300 text-amber-800 leading-none flex-shrink-0 whitespace-nowrap ml-1 shadow-sm">
                                    Published &bull; View Only
                                </span>
                            )}
                            {/* Settings button with label */}
                            <button
                                id="flow-btn-settings"
                                type="button"
                                title={isPublished ? 'View only — create a draft to edit settings' : 'Edit flow name & description'}
                                disabled={isPublished}
                                onClick={() => {
                                    if (isPublished) return;
                                    setEditFlowName(flowName);
                                    setEditFlowDescription(flowDescription);
                                    setShowFlowSettings(true);
                                }}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors flex-shrink-0 border ${
                                    isPublished
                                        ? 'text-gray-300 cursor-not-allowed border-transparent opacity-50'
                                        : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700 border-transparent hover:border-gray-200'
                                }`}
                            >
                                <Settings className="w-3 h-3" />
                                Settings
                            </button>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {isStatusLoading ? (
                        // ── Status not yet known: render nothing until API responds ──────────
                        null
                    ) : forceReadOnly ? (
                        // ── View-only toolbar: only Run App + Fullscreen ──────────────────
                        <>
                            {onRun && (
                                <Button
                                    id="flow-btn-run-app"
                                    size="sm"
                                    className={`gap-2 ${externalIsExecuting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white shadow-sm`}
                                    onClick={onRun}
                                    disabled={externalIsExecuting}
                                    title="Run in production mode"
                                >
                                    {externalIsExecuting
                                        ? <><Loader2 className="h-4 w-4 animate-spin" /> Running…</>
                                        : <><Play className="h-4 w-4" fill="currentColor" /> Run App</>}
                                </Button>
                            )}
                            <button
                                onClick={() => setIsFullScreen(!isFullScreen)}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                                title={isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
                            >
                                {isFullScreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
                            </button>
                        </>
                    ) : (
                        // ── Full edit toolbar ─────────────────────────────────────────────
                        <>
                    {!isPublished && (
                        <>
                            <Button
                                size="icon"
                                variant="outline"
                                className="text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100 h-8 w-10"
                                onClick={() => window.open('/node_user_guide.html', '_blank')}
                                title="View Node User Guide"
                            >
                                <BookOpen className="h-4 w-4" />
                            </Button>
                            <div className="w-px h-6 bg-gray-300 mx-1" />
                            {/* See Versions button (merged: version history + load snapshot) */}
                            {flowId && (
                                <Button
                                    id="flow-btn-versions"
                                    size="icon"
                                    variant="outline"
                                    className="text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100 h-8 w-10"
                                    onClick={() => setShowLoadSnapshot(true)}
                                    title="See version history and load snapshots"
                                >
                                    <History className="h-4 w-4" />
                                </Button>
                            )}
                            <div className="w-px h-6 bg-gray-300 mx-1" />
                            <Button
                                id="flow-btn-align"
                                size="icon"
                                variant="outline"
                                className="text-gray-600 h-8 w-10"
                                onClick={onAutoLayout}
                                title="Auto Align Nodes"
                                disabled={isPublished}
                            >
                                <LayoutTemplate className="h-4 w-4" />
                            </Button>
                            {/* <div className="w-px h-6 bg-gray-300 mx-1"></div> */}
                            <Button
                                id="flow-btn-variables"
                                size="icon"
                                variant="outline"
                                className="text-gray-700 bg-yellow-50 border-yellow-200 hover:bg-yellow-100 h-8 w-10"
                                onClick={() => !isPublished && setShowVarModal(true)}
                                disabled={isPublished}
                                title={isPublished ? 'View only — create a draft to edit variables' : 'Variables'}
                            >
                                <Braces className="h-4 w-4 text-yellow-600" />
                            </Button>
                            <Button
                                id="flow-btn-schedule"
                                size="icon"
                                variant="outline"
                                className="text-gray-600 h-8 w-10"
                                onClick={() => setShowSchedule(true)}
                                title="Schedule"
                            >
                                <Calendar className="h-4 w-4" />
                            </Button>
                            <div className="w-px h-6 bg-gray-300 mx-1"></div>
                            <Button
                                id="flow-btn-save"
                                size="sm"
                                variant="outline"
                                className={`gap-2 text-gray-600 ${hasUnsavedChanges ? 'border-yellow-400' : ''}`}
                                onClick={() => void saveFlow()}
                                disabled={isSaving}
                                title="Save"
                            >
                                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                <span className="hidden sm:inline">Save</span>
                                {hasUnsavedChanges && <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />}
                            </Button>
                        </>
                    )}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                id="flow-btn-share"
                                size="icon"
                                variant="outline"
                                className="text-gray-600 h-8 w-10"
                                title="Share"
                            >
                                <Share2 className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56 bg-white dark:bg-slate-900 shadow-xl border-gray-100 dark:border-slate-800">
                            <DropdownMenuItem onClick={handleExport} className="cursor-pointer">
                                <Download className="w-4 h-4 mr-2" /> Export as JSON Template
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setIsShareSheetOpen(true)} className="cursor-pointer">
                                <Rocket className="w-4 h-4 mr-2" /> Embed / Publish URL
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>


                    {/* View Switcher Removed */}
                    {/* <div className="flex bg-gray-100 p-0.5 rounded-lg border border-gray-200">...</div> */}


                    {/* Last Run Report - Hidden
                    <Button
                        size="sm"
                        variant="outline"
                        className="ml-2 gap-2 text-gray-700 bg-gray-50 border-gray-200 hover:bg-gray-100"
                        onClick={() => setShowReportModal(true)}
                        disabled={!lastRunReport}
                    >
                        <LayoutTemplate className="h-4 w-4 text-purple-600" />
                        <span className="hidden sm:inline">Last Run Report</span>
                    </Button>
                    */}
                    {/* Show Publish only on drafts */}
                    {!isPublished && (
                        <Button
                            id="flow-btn-publish"
                            size="sm"
                            variant="outline"
                            className="gap-2 text-purple-700 bg-purple-50 border-purple-200 hover:bg-purple-100"
                            onClick={() => setShowPublishModal(true)}
                            title="Publish flow"
                        >
                            <Rocket className="h-4 w-4 text-purple-600" />
                            <span className="hidden sm:inline">Publish</span>
                        </Button>
                    )}
                    {/* Show Edit Flow only on published view mode */}
                    {isPublished && !forceReadOnly && (
                        <Button
                            id="flow-btn-edit"
                            size="sm"
                            variant="outline"
                            className="gap-2 text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100"
                            onClick={handleCreateDraft}
                            disabled={isCreatingDraft}
                            title="Unlock flow for editing"
                        >
                            {isCreatingDraft ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePen className="h-4 w-4 text-blue-600" />}
                            <span className="hidden sm:inline">Edit Flow</span>
                        </Button>
                    )}
                    <div className="w-px h-6 bg-gray-300 mx-1"></div>
                    {isRunning && (
                        <Button
                            size="icon"
                            variant="destructive"
                            className="h-8 w-10"
                            onClick={handleCancel}
                            title="Cancel Run"
                        >
                            <Ban className="h-4 w-4" />
                        </Button>
                    )}

                    {/* Row Limit Selector — hidden in view-only (forceReadOnly) mode */}
                    {!forceReadOnly && (
                    <div className="relative" ref={rowLimitMenuRef}>
                        <Button
                            id="flow-btn-row-limit"
                            size="sm"
                            variant="outline"
                            className={`gap-2 ${rowLimit
                                ? 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'
                                : 'text-gray-600'
                                }`}
                            onClick={() => setShowRowLimitMenu(!showRowLimitMenu)}
                        >
                            <AlertTriangle className={`h-4 w-4 ${rowLimit ? 'text-amber-500' : 'text-gray-400'}`} />
                            <span className="hidden sm:inline">{rowLimit ? `${rowLimit.toLocaleString()} rows` : 'No Limit'}</span>
                            <ChevronDown className="h-3 w-3" />
                        </Button>
                        {showRowLimitMenu && (
                            <div className="absolute right-0 top-full mt-1 z-50 bg-white shadow-lg border border-gray-200 rounded-lg py-1 min-w-[170px]">
                                <div className="px-3 py-1.5 text-xs text-gray-400 font-semibold uppercase">Source Row Limit</div>
                                {[1000, 5000, 10000, null].map((limit) => (
                                    <button
                                        key={String(limit)}
                                        className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${rowLimit === limit
                                            ? 'bg-blue-50 text-blue-700'
                                            : 'hover:bg-gray-50 text-gray-700'
                                            }`}
                                        onClick={() => { setRowLimit(limit); setShowRowLimitMenu(false); }}
                                    >
                                        {limit ? `${limit.toLocaleString()} rows` : '⚡ No Limit (Full Data)'}
                                        {rowLimit === limit && <span className="ml-auto text-blue-500">✓</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    )}

                    {/* Run split-button — hidden in view-only (forceReadOnly) mode */}
                    {!forceReadOnly && (
                    <div className="flex items-center">
                        <Button
                            id="flow-btn-run"
                            size="sm"
                            className={`gap-2 rounded-r-none border-r border-blue-400 ${isRunning ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'} text-white shadow-sm`}
                            onClick={runFlow}
                            disabled={isRunning || getNodes().length === 0}
                            title="Run Interactive Test (Slower, Limited Data)"
                        >
                            <Play className={`h-4 w-4 ${isRunning ? 'animate-pulse' : ''}`} />
                            {isRunning ? 'Running...' : 'Run'}
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    size="sm"
                                    className={`rounded-l-none px-2 ${isRunning ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'} text-white shadow-sm`}
                                    disabled={isRunning || getNodes().length === 0}
                                >
                                    <ChevronDown className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Execution Mode</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={runFlow}>
                                    <Play className="h-4 w-4 mr-2" />
                                    Run <span className="ml-2 text-xs text-slate-400">(Interactive)</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={runFlowProduction}>
                                    <Rocket className="h-4 w-4 mr-2" />
                                    Production Run <span className="ml-2 text-xs text-slate-400">(Background)</span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                    )}
                    <button
                        onClick={() => setIsFullScreen(!isFullScreen)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                        title={isFullScreen ? "Exit Full Screen" : "Enter Full Screen"}
                    >
                        {isFullScreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
                    </button>
                    </>
                    )}
                </div>
            </div>
            {/* end edit toolbar */}

            {/* Main Workspace */}
            <div className="flex-1 flex overflow-hidden">
            {/* FlowSidebar toolbox — hidden in view-only mode and while status is loading */}
                {!isStatusLoading && !isPublished && !forceReadOnly && <FlowSidebar onFileUploaded={(result: any) => {
                    // Insert fileSource node at canvas center when uploaded from sidebar
                    const newNode = {
                        id: `file-src-${crypto.randomUUID()}`,
                        type: 'fileSource',
                        position: { x: 200 + Math.random() * 100, y: 150 + Math.random() * 100 },
                        data: {
                            label: result.fileName,
                            fileName: result.fileName,
                            filePath: result.filePath,
                            fileType: result.fileType,
                            rowCount: result.rowCount ?? 0,
                            columns: result.columns ?? [],
                            preview: result.preview ?? [],
                            connectionMode: 'file',
                        },
                    };
                    setNodes((nds: any) => [...nds, newNode]);
                }} />
                }

                <div id="flow-canvas-area" className="flex-1 flex flex-col relative bg-gray-50 dark:bg-slate-950 h-full min-h-0">
                    {flowCanvasLoading && (
                        <div
                            className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/85 dark:bg-slate-950/85 backdrop-blur-[2px] pointer-events-auto"
                            aria-busy="true"
                            aria-live="polite"
                        >
                            <Loader2 className="h-10 w-10 animate-spin text-blue-600 dark:text-blue-400" aria-hidden />
                            <p className="text-sm text-gray-600 dark:text-gray-400">Loading flow…</p>
                        </div>
                    )}
                    <div className="flex-1 flex flex-row relative min-h-0">
                        <div className="flex-1 relative min-h-0">

                        {activeView === 'canvas' ? (
                            <FlowCanvas
                                onSelectionChange={setSelectedNode}
                                nodes={nodes}
                                edges={edges}
                                onNodesChange={onNodesChange}
                                onEdgesChange={onEdgesChangeWithStatusClear}
                                onConnect={onConnect}
                                setNodes={setNodes}
                                setEdges={setEdges}
                                onRunNode={handleRunNode}
                                readOnly={isPublished}
                                onLockToggle={() => isPublished ? handleCreateDraft() : setShowPublishModal(true)}
                                isTogglingLock={isCreatingDraft}
                            />
                        ) : (
                            <DashboardGrid
                                visualNodes={nodes.filter(n => n.type === 'visual')}
                                flowId={flowId || ''}
                                onNodeUpdate={handleNodeUpdate}
                            />
                        )}
                    </div>
                        {/* Right Panel (Unpinned Console) */}
                        {activeView === 'canvas' && !isPinned && (
                            <BottomPanel
                                logs={logs}
                                nodeStats={nodeStats}
                                previews={nodePreviews}
                                selectedNodeId={selectedNode?.id || null}
                                isRunning={isRunning}
                                isOpen={isBottomPanelOpen}
                                onToggle={() => setIsBottomPanelOpen(!isBottomPanelOpen)}
                                isPinned={isPinned}
                                onTogglePin={() => setIsPinned(!isPinned)}
                                consoleSize={consoleSize}
                                onResize={setConsoleSize}
                            />
                        )}
                    </div>
                    {/* Bottom Panel (Pinned Console) */}
                    {activeView === 'canvas' && isPinned && (
                        <BottomPanel
                            logs={logs}
                            nodeStats={nodeStats}
                            previews={nodePreviews}
                            selectedNodeId={selectedNode?.id || null}
                            isRunning={isRunning}
                            isOpen={isBottomPanelOpen}
                            onToggle={() => setIsBottomPanelOpen(!isBottomPanelOpen)}
                            isPinned={isPinned}
                            onTogglePin={() => setIsPinned(!isPinned)}
                            consoleSize={consoleSize}
                            onResize={setConsoleSize}
                        />
                    )}
                </div>
                {/* Properties Sidebar — hidden in published view mode */}
                {activeView === 'canvas' && selectedNode && !isPublished && (
                    <ResizableSidebar
                        isOpen={isPropertiesOpen}
                        onToggle={() => setIsPropertiesOpen(!isPropertiesOpen)}
                        title="Configuration"
                        initialWidth={300}
                        onInfoClick={() => setShowInfoPanel(v => !v)}
                        showInfo={showInfoPanel}
                    >
                        <PropertiesPanel
                            flowVariables={flowVariables}
                            connections={connections}
                            flowId={flowId || ''}
                            onShowLeaderboard={(nodeId: string) => {
                                setLeaderboardNodeId(nodeId);
                                setShowLeaderboardModal(true);
                            }}
                            onRunToNode={handleRunNode}
                            onNodeUpdate={handleNodeUpdate}
                            previewData={selectedNode ? nodePreviews[selectedNode.id] : undefined}
                            showInfoPanel={showInfoPanel}
                            readOnly={isPublished}
                        />
                    </ResizableSidebar>
                )}

            </div >

            {/* Leaderboard Modal */}
            <FlowLeaderboardModal
                open={showLeaderboardModal}
                onOpenChange={setShowLeaderboardModal}
                executionId={currentExecutionId || lastRunReport?.id || null}
                nodeId={leaderboardNodeId}
            />

            {/* Flow Settings Dialog — edit name & description */}
            <Dialog open={showFlowSettings} onOpenChange={setShowFlowSettings}>
                <DialogContent className="sm:max-w-[440px] bg-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Settings className="h-4 w-4 text-slate-500" />
                            Flow Settings
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-2">
                        <div className="grid gap-2">
                            <Label htmlFor="flow-settings-name">Flow Name</Label>
                            <Input
                                id="flow-settings-name"
                                value={editFlowName}
                                onChange={(e) => setEditFlowName(e.target.value)}
                                placeholder="e.g. Monthly Data Sync"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="flow-settings-desc" className="flex items-center gap-1.5">
                                <FileText className="w-3.5 h-3.5 text-slate-400" />
                                Description
                            </Label>
                            <Textarea
                                id="flow-settings-desc"
                                value={editFlowDescription}
                                onChange={(e) => setEditFlowDescription(e.target.value)}
                                placeholder="Describe what this flow does..."
                                className="h-24 resize-none"
                            />
                            <p className="text-[11px] text-slate-400">
                                This description pre-fills the Publish modal and is shown in the bundle version picker.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowFlowSettings(false)}>Cancel</Button>
                        <Button
                            id="flow-settings-save-btn"
                            onClick={async () => {
                                const trimmedName = editFlowName.trim();
                                if (!trimmedName) return;
                                setFlowName(trimmedName);
                                setFlowDescription(editFlowDescription.trim());
                                setShowFlowSettings(false);
                                // Persist to backend
                                if (flowId) {
                                    try {
                                        const t = getAuthToken();
                                        await fetch(getApiUrl(`/api/flows/${flowId}`), {
                                            method: 'PATCH',
                                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${t}` },
                                            body: JSON.stringify({ name: trimmedName, description: editFlowDescription.trim() })
                                        });
                                    } catch { /* non-critical */ }
                                } else {
                                    // It's a new flow, actually create it now
                                    await saveFlow(trimmedName, editFlowDescription.trim());
                                }
                            }}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Publish App Modal */}
            <PublishAppModal
                open={showPublishModal}
                onOpenChange={setShowPublishModal}
                defaultName={flowName}
                defaultDescription={flowDescription}
                initialVersionLabel={`v${(flowVersionNumber || 0) + 1}`}
                onPublish={handlePublishApp}
            />

            {/* Schedule Modal */}
            <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
                <DialogContent className="bg-white">
                    <DialogHeader>
                        <DialogTitle>Schedule Flow Execution</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right">Frequency</Label>
                            <Select onValueChange={setScheduleCron} value={scheduleCron}>
                                <SelectTrigger className="col-span-3">
                                    <SelectValue placeholder="Select frequency" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="0 0 * * * ?">Every Hour</SelectItem>
                                    <SelectItem value="0 0 0 * * ?">Daily (Midnight)</SelectItem>
                                    <SelectItem value="0 0 0 * * MON">Weekly (Monday)</SelectItem>
                                    <SelectItem value="custom">Custom Cron...</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {scheduleCron === 'custom' && (
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label className="text-right">Cron</Label>
                                <Input
                                    className="col-span-3"
                                    placeholder="0 0 * * * ?"
                                    onChange={(e) => setScheduleCron(e.target.value)}
                                />
                            </div>
                        )}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right">Status</Label>
                            <div className="flex items-center space-x-2 col-span-3">
                                <input
                                    type="checkbox"
                                    checked={isScheduleActive}
                                    onChange={(e) => setIsScheduleActive(e.target.checked)}
                                    className="rounded border-gray-300 transform scale-125"
                                />
                                <span className="text-sm text-gray-500">Enable Schedule</span>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={saveSchedule}>Save Schedule</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog >

            {/* Variable Manager Modal */}
            <Dialog open={showVarModal} onOpenChange={setShowVarModal}>
                <DialogContent className="max-w-2xl bg-white">
                    <DialogHeader>
                        <DialogTitle>Flow Variables</DialogTitle>
                        <p className="text-xs text-gray-500 mt-1">Define reusable values that can be referenced in any node using <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">{'{{var.variable_name}}'}</code></p>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {/* Quick Start Examples */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs">
                            <div className="font-semibold text-blue-900 mb-2">💡 Quick Start Examples</div>
                            <div className="space-y-1 text-blue-800">
                                <div>• API Dates: <code className="bg-white px-1 rounded">{'{{var.start_date}}'}</code></div>
                                <div>• Auth Tokens: <code className="bg-white px-1 rounded">{'{{var.api_key}}'}</code></div>
                                <div>• Use in URLs: <code className="bg-white px-1 rounded text-[10px]">https://api.example.com?date={'{{var.start_date}}'}</code></div>
                            </div>
                        </div>

                        <div className="bg-gray-50 p-2 rounded border grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500">
                            <div className="col-span-4">Name</div>
                            <div className="col-span-3">Type</div>
                            <div className="col-span-4">Initial Value</div>
                            <div className="col-span-1"></div>
                        </div>
                        <div className="max-h-[300px] overflow-y-auto space-y-2">
                            {flowVariables.map((v, idx) => (
                                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                                    <Input className="col-span-4 h-8 text-xs" value={v.name} onChange={e => {
                                        const newVars = [...flowVariables]; newVars[idx].name = e.target.value; setFlowVariables(newVars);
                                    }} placeholder="Variable Name" />
                                    <Select value={v.type} onValueChange={val => {
                                        const newVars = [...flowVariables]; newVars[idx].type = val; setFlowVariables(newVars);
                                    }}>
                                        <SelectTrigger className="col-span-3 h-8 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="String">String</SelectItem>
                                            <SelectItem value="Date">Date</SelectItem>
                                            <SelectItem value="Integer">Integer</SelectItem>
                                            <SelectItem value="Double">Double</SelectItem>
                                            <SelectItem value="Boolean">Boolean</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {v.type === 'Date' ? (
                                        <input
                                            type="date"
                                            className="col-span-4 h-8 text-xs px-2 border border-gray-200 rounded-md"
                                            value={v.value}
                                            onChange={e => {
                                                const newVars = [...flowVariables]; newVars[idx].value = e.target.value; setFlowVariables(newVars);
                                            }}
                                        />
                                    ) : (
                                        <Input className="col-span-4 h-8 text-xs" value={v.value} onChange={e => {
                                            const newVars = [...flowVariables]; newVars[idx].value = e.target.value; setFlowVariables(newVars);
                                        }} placeholder="Value" />
                                    )}
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => {
                                        const newVars = [...flowVariables]; newVars.splice(idx, 1); setFlowVariables(newVars);
                                    }}><Trash2 className="h-4 w-4" /></Button>
                                </div>
                            ))}
                        </div>
                        <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => setFlowVariables([...flowVariables, { name: 'NewVar', type: 'String', value: '' }])}>+ Add Variable</Button>
                    </div>
                </DialogContent>
            </Dialog >

            {/* Report Modal */}
            <FlowRunReportModal
                open={showReportModal}
                onOpenChange={setShowReportModal}
                report={lastRunReport}
            />


            {/* Unsaved Changes Dialog */}
            <ConfirmModal
                isOpen={showExitWarning}
                onClose={() => setShowExitWarning(false)}
                onConfirm={() => {
                    setShowExitWarning(false);
                    if (onBack) onBack();
                }}
                title="Unsaved Changes"
                zIndex={50}
            >
                You have unsaved changes. Are you sure you want to discard changes and go back?
            </ConfirmModal>

            {/* Version History Panel */}
            {showVersionsPanel && flowId && (
                <FlowVersionsPanel
                    flowId={flowId}
                    flowName={flowName}
                    mode="view"
                    onClose={() => setShowVersionsPanel(false)}
                />
            )}

            {/* Load Snapshot Panel */}
            {showLoadSnapshot && flowId && (
                <FlowVersionsPanel
                    flowId={flowId}
                    flowName={flowName}
                    mode="load"
                    onClose={() => setShowLoadSnapshot(false)}
                    onLoadSnapshot={async (version) => {
                        try {
                            // List endpoint omits flow_snapshot for performance.
                            // Must fetch the full detail endpoint to get the snapshot JSON.
                            const token = getAuthToken();
                            const res = await fetch(
                                getApiUrl(`/api/flows/${flowId}/versions/${version.id}`),
                                { headers: { 'Authorization': `Bearer ${token}` } }
                            );
                            if (!res.ok) throw new Error('Failed to fetch version detail');
                            const detail = await res.json();
                            const snapshotRaw = detail?.flow_snapshot;
                            if (!snapshotRaw) throw new Error('No snapshot data in this version');
                            const graph = typeof snapshotRaw === 'string' ? JSON.parse(snapshotRaw) : snapshotRaw;
                            if (graph?.nodes) setNodes(graph.nodes.map((n: any) => ({
                                ...n,
                                data: { ...n.data, executionStatus: undefined, rowsAffected: undefined, previewSnippet: undefined }
                            })));
                            if (graph?.edges) setEdges(graph.edges);
                            if (graph?.variables) setFlowVariables(graph.variables);
                            const label = version.version || `v${version.version_number}`;
                            setSnapshotBanner(`Loaded snapshot: ${label}`);
                            setHasUnsavedChanges(true);
                            addNotification(`Snapshot ${label} loaded into editor`, 'success');
                        } catch (e: any) {
                            addNotification(e?.message || 'Failed to load snapshot into editor', 'error');
                        }
                        setShowLoadSnapshot(false);
                    }}
                />
            )}
            {/* Share Sheet */}
            <DashboardShareSheet
                open={isShareSheetOpen}
                dashboardId={flowId}
                dashboardTitle={flowName}
                isPublished={isPublished}
                canShareWithAll={!!(user?.roleId && ['r1', 'r2', 'r3', 'r4', 'r7'].includes(user.roleId))}
                canShareWithOrg={!!(user?.roleId && ['r1', 'r2', 'r3', 'r4'].includes(user.roleId))}
                sharedWith={sharedWith}
                publishShareMode={publishShareMode}
                availableUsers={shareUserCandidates}
                onClose={() => setIsShareSheetOpen(false)}
                onApplySettings={applyShareSettings}
                onCopyLink={async (link) => {
                    try {
                        await navigator.clipboard.writeText(link);
                        addNotification('Share link copied to clipboard', 'success');
                    } catch {
                        addNotification('Failed to copy link', 'error');
                    }
                }}
            />
        </div>
    );
}
